import { useMutation, useQueryClient } from '@tanstack/react-query';

import { schedulerApi } from '../api';
import { FirestoreRoutineActivity } from '../types';
import { Routine } from '@demind-inc/core';
import { trackEventMixpanel } from '../../utils';
import { useRecoilState } from 'recoil';
import { eventsSnackBarAtom } from '../recoil';
import { translateI18n } from '../../i18n';

interface UseAddRoutineParams {
  routineId: string;
  routineActivity: FirestoreRoutineActivity;
}

export const useAddRoutine = () => {
  const queryClient = useQueryClient();
  const [_, setEventsSnackbar] = useRecoilState(eventsSnackBarAtom);

  const addRoutineMutation = useMutation({
    mutationFn: ({ routineId, routineActivity }: UseAddRoutineParams) => {
      return schedulerApi
        .createRoutineActivity(routineId, routineActivity)
        .then(({ data }) => data);
    },
    onMutate: async ({ routineId, routineActivity }: UseAddRoutineParams) => {
      setEventsSnackbar({
        message: translateI18n('routine.snackbar.creating'),
        autoHideDuration: 'disable',
        type: 'loading',
      });

      await queryClient.cancelQueries({
        queryKey: ['lifestack.scheduler.routine', { routineId }],
      });
      queryClient.setQueriesData<Routine>(
        { queryKey: ['lifestack.scheduler.routine', { routineId }] },
        (prev) => {
          return {
            ...prev,
            routineActivities: [
              ...(prev?.routineActivities ?? []),
              {
                ...routineActivity,
                routineActivityId: 'creating_activity',
              },
            ],
          } as Routine;
        }
      );
    },
    onSuccess: (_, { routineId, routineActivity }) => {
      setEventsSnackbar({ message: translateI18n('routine.snackbar.created'), type: 'success' });
      trackEventMixpanel('create_routine', {
        routineId,
        name: routineActivity.name,
      });
      queryClient.invalidateQueries({ queryKey: ['lifestack.scheduler.routine', { routineId }] });
      queryClient.invalidateQueries({ queryKey: ['lifestack.calendar.events'] });
    },
    onError: () => {
      setEventsSnackbar({ message: translateI18n('routine.snackbar.createFailed'), type: 'error' });
    },
  });

  return {
    addRoutine: addRoutineMutation.mutateAsync,
    ...addRoutineMutation,
  };
};
